import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, orderBy, addDoc, doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../firebaseconfig';
import { useAuth } from '../../contexts/AuthContext';
import { USER_ROLES } from '../../utils/roles';
import StaffList from './StaffList';
import { useNavigate } from 'react-router-dom';
import TransferModal from './TransferModal';
import AddStaffModal from './AddStaffModal';
import './Savings.css';

const Savings = () => {
  const [savings, setSavings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [amount, setAmount] = useState('');
  const [transactionType, setTransactionType] = useState('deposit');
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showAddStaffModal, setShowAddStaffModal] = useState(false);
  const { currentUser, userRole } = useAuth();
  const navigate = useNavigate();
  const isAdmin = userRole === USER_ROLES.ADMIN;

  useEffect(() => {
    if (isAdmin) {
      if (selectedStaff) {
        fetchSavings(selectedStaff.id);
      } else {
        setSavings([]);
        setLoading(false);
      }
    } else {
      fetchSavings(currentUser.uid);
    }
  }, [currentUser, selectedStaff]);

  const fetchSavings = async (userId) => {
    try {
      setLoading(true);
      const savingsRef = collection(db, 'savings');
      const q = query(
        savingsRef,
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const savingsList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date()
      }));

      setSavings(savingsList);
      setError('');
    } catch (err) {
      console.error('Error fetching savings:', err);
      setError('Failed to load savings data');
    } finally {
      setLoading(false);
    }
  };

  const handleTransaction = async (e) => {
    e.preventDefault();
    if (!amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    if (isAdmin && !selectedStaff) {
      setError('Please select a staff member');
      return;
    }

    const userId = isAdmin ? selectedStaff.id : currentUser.uid;
    const currentBalance = calculateBalance(savings);

    if (transactionType === 'withdrawal' && parseFloat(amount) > currentBalance) {
      setError('Insufficient balance for withdrawal');
      return;
    }

    try {
      const savingsRef = collection(db, 'savings');
      const newTransaction = {
        userId,
        amount: parseFloat(amount),
        type: transactionType,
        createdAt: Timestamp.now(),
        createdBy: currentUser.email,
        processedBy: isAdmin ? currentUser.email : null
      };

      await addDoc(savingsRef, newTransaction);
      setSuccess(`${transactionType.charAt(0).toUpperCase() + transactionType.slice(1)} successful!`);
      setAmount('');
      fetchSavings(userId);
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error processing transaction:', err);
      setError('Failed to process transaction');
    }
  };

  const handleTransfer = async (toStaff, transferAmount, description) => {
    if (!selectedStaff || !toStaff || parseFloat(transferAmount) <= 0) {
      setError('Invalid transfer details');
      return;
    }

    const currentBalance = calculateBalance(savings);
    if (parseFloat(transferAmount) > currentBalance) {
      setError('Insufficient balance for transfer');
      return;
    }

    try {
      const savingsRef = collection(db, 'savings');
      const timestamp = Timestamp.now();

      // Create withdrawal transaction for sender
      const withdrawalTransaction = {
        userId: selectedStaff.id,
        amount: parseFloat(transferAmount),
        type: 'transfer_out',
        createdAt: timestamp,
        createdBy: currentUser.email,
        processedBy: currentUser.email,
        transferToUser: toStaff.id,
        transferToName: toStaff.displayName || toStaff.email,
        description
      };

      // Create deposit transaction for receiver
      const depositTransaction = {
        userId: toStaff.id,
        amount: parseFloat(transferAmount),
        type: 'transfer_in',
        createdAt: timestamp,
        createdBy: currentUser.email,
        processedBy: currentUser.email,
        transferFromUser: selectedStaff.id,
        transferFromName: selectedStaff.displayName || selectedStaff.email,
        description
      };

      await Promise.all([
        addDoc(savingsRef, withdrawalTransaction),
        addDoc(savingsRef, depositTransaction)
      ]);

      setSuccess('Transfer completed successfully!');
      setShowTransferModal(false);
      fetchSavings(selectedStaff.id);
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error processing transfer:', err);
      setError('Failed to process transfer');
    }
  };

  const calculateBalance = (transactions) => {
    return transactions.reduce((total, transaction) => {
      if (transaction.type === 'deposit' || transaction.type === 'transfer_in') {
        return total + transaction.amount;
      } else if (transaction.type === 'withdrawal' || transaction.type === 'transfer_out') {
        return total - transaction.amount;
      }
      return total;
    }, 0);
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN'
    }).format(amount);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleStaffSelect = (staff) => {
    setSelectedStaff(staff);
    setError('');
    navigate(`/savings/${staff.id}`);
  };

  const getTransactionLabel = (type) => {
    const labels = {
      deposit: 'Deposit',
      withdrawal: 'Withdrawal',
      transfer_in: 'Transfer Received',
      transfer_out: 'Transfer Sent'
    };
    return labels[type] || type;
  };

  const handleAddStaffSuccess = () => {
    setSuccess('Staff member created successfully!');
    setTimeout(() => setSuccess(''), 3000);
  };

  if (!isAdmin && userRole !== USER_ROLES.USER) {
    return (
      <div className="unauthorized-message">
        You are not authorized to access the savings feature.
      </div>
    );
  }

  return (
    <div className="savings-container">
      <div className="savings-header">
        <div className="header-top">
          <h2>{isAdmin ? 'Staff Savings Management' : 'My Savings'}</h2>
          {isAdmin && (
            <button
              className="add-staff-button"
              onClick={() => setShowAddStaffModal(true)}
            >
              Add New Staff
            </button>
          )}
        </div>
        {isAdmin && (
          <StaffList
            onSelectStaff={handleStaffSelect}
            selectedStaffId={selectedStaff?.id}
          />
        )}
        {((!isAdmin && savings.length > 0)) && (
          <div className="savings-balance">
            <h3>
              {'Your Balance'}
            </h3>
            <p>{formatAmount(calculateBalance(savings))}</p>
          </div>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {(!isAdmin || (isAdmin && selectedStaff)) && (
        <div className="transaction-form">
          <h3>New Transaction</h3>
          <form onSubmit={handleTransaction}>
            <div className="form-group">
              <label htmlFor="amount">Amount (NGN)</label>
              <input
                type="number"
                id="amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                min="0"
                step="0.01"
                required
                placeholder="Enter amount"
              />
            </div>

            <div className="form-group">
              <label htmlFor="type">Transaction Type</label>
              <select
                id="type"
                value={transactionType}
                onChange={(e) => setTransactionType(e.target.value)}
                required
              >
                <option value="deposit">Deposit</option>
                <option value="withdrawal">Withdrawal</option>
              </select>
            </div>

            <button type="submit" className="submit-button">
              Process Transaction
            </button>

            {isAdmin && selectedStaff && (
              <button
                type="button"
                className="transfer-button"
                onClick={() => setShowTransferModal(true)}
              >
                Transfer
              </button>
            )}
          </form>
        </div>
      )}

      {savings.length > 0 && (
        <div className="transactions-list">
          <h3>Transaction History</h3>
          <div className="transactions-table">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Amount</th>
                  <th>Description</th>
                  {isAdmin && <th>Processed By</th>}
                </tr>
              </thead>
              <tbody>
                {savings.map((transaction) => (
                  <tr key={transaction.id}>
                    <td>{formatDate(transaction.createdAt)}</td>
                    <td className={`transaction-type ${transaction.type}`}>
                      {getTransactionLabel(transaction.type)}
                      {transaction.type === 'transfer_in' && transaction.transferFromName && (
                        <span className="transfer-detail"> from {transaction.transferFromName}</span>
                      )}
                      {transaction.type === 'transfer_out' && transaction.transferToName && (
                        <span className="transfer-detail"> to {transaction.transferToName}</span>
                      )}
                    </td>
                    <td className={`transaction-amount ${transaction.type}`}>
                      {formatAmount(transaction.amount)}
                    </td>
                    <td>{transaction.description || '-'}</td>
                    {isAdmin && <td>{transaction.processedBy || transaction.createdBy}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showTransferModal && (
        <TransferModal
          onClose={() => setShowTransferModal(false)}
          onTransfer={handleTransfer}
          fromStaff={selectedStaff}
          currentBalance={calculateBalance(savings)}
        />
      )}

      {showAddStaffModal && (
        <AddStaffModal
          onClose={() => setShowAddStaffModal(false)}
          onSuccess={handleAddStaffSuccess}
        />
      )}
    </div>
  );
};

export default Savings;